import { supabase } from "@/integrations/supabase/client";
import {
  classifyTraffic,
  type ConversionEvent,
  type TrafficSource,
} from "../../supabase/functions/_shared/traffic-source";

export { classifyTraffic, isGoogleHost, CONVERSION_EVENTS } from "../../supabase/functions/_shared/traffic-source";
export type { ConversionEvent, TrafficSource } from "../../supabase/functions/_shared/traffic-source";

const VISITOR_KEY = "dm_visitor_id";
const FIRST_TOUCH_KEY = "dm_first_touch";
const SESSION_KEY = "dm_session_id";

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function safeGet(store: Storage | undefined, key: string): string | null {
  try { return store?.getItem(key) ?? null; } catch { return null; }
}
function safeSet(store: Storage | undefined, key: string, value: string) {
  try { store?.setItem(key, value); } catch { /* storage blocked */ }
}

export function getVisitorId(): string {
  let id = safeGet(window.localStorage, VISITOR_KEY);
  if (!id) { id = randomId(); safeSet(window.localStorage, VISITOR_KEY, id); }
  return id;
}

/** First-touch: once a non-direct source is recorded it is kept. */
export function resolveFirstTouch(stored: TrafficSource | null, current: TrafficSource): TrafficSource {
  if (!stored) return current;
  if (stored.source === "direct" && current.source !== "direct") return current;
  return stored;
}

export function getFirstTouch(): TrafficSource {
  const raw = safeGet(window.localStorage, FIRST_TOUCH_KEY);
  try { return raw ? JSON.parse(raw) : { source: "direct", medium: "none", referrerHost: "" }; }
  catch { return { source: "direct", medium: "none", referrerHost: "" }; }
}

/** Records one visit per browser session. Safe to call on every page load. */
export function trackVisit() {
  if (typeof window === "undefined") return;
  if (safeGet(window.sessionStorage, SESSION_KEY)) return;
  const sessionId = randomId();
  safeSet(window.sessionStorage, SESSION_KEY, sessionId);

  const current = classifyTraffic(window.location.search, document.referrer);
  const storedRaw = safeGet(window.localStorage, FIRST_TOUCH_KEY);
  let stored: TrafficSource | null = null;
  try { stored = storedRaw ? JSON.parse(storedRaw) : null; } catch { stored = null; }
  safeSet(window.localStorage, FIRST_TOUCH_KEY, JSON.stringify(resolveFirstTouch(stored, current)));

  void supabase.functions.invoke("track-visit", {
    body: {
      kind: "visit",
      visitor_id: getVisitorId(),
      session_id: sessionId,
      search: window.location.search,
      referrer: document.referrer,
      landing_path: window.location.pathname,
    },
  }).catch(() => undefined);
}

/** Fire-and-forget conversion tied to the visitor's first-touch source. */
export function trackConversion(event: ConversionEvent) {
  if (typeof window === "undefined") return;
  const ft = getFirstTouch();
  void supabase.functions.invoke("track-visit", {
    body: { kind: "conversion", visitor_id: getVisitorId(), event, source: ft.source, medium: ft.medium },
  }).catch(() => undefined);
}
